import { useTranslation } from "react-i18next";
import { LateralDialog } from "../../../../utils/components/dialogs/LateralDialog";
import {
	ModalNavigator,
	useModalNavigator,
	useModalRoute,
} from "../../../../utils/components/ModalNavigator/ModalNavigator";
import { Button, Typography } from "@mui/material";
import LoadingIconButton from "../../../../utils/components/buttons/LoadingIconButton";
import CertiblokIcon from "../../../../utils/components/icons/CertiblokIcon";
import {
	TreeViewDocumentPickerContextProvider,
	useInitTreeViewDocumentPicker,
} from "../../../../utils/components/TreeViewDocumentPicker/controllers/TreeViewDocumentPickerContext";
import { TreeViewDocumentPickerAutocomplete } from "../../../../utils/components/TreeViewDocumentPicker/components/TreeViewDocumentPickerAutocomplete";
import { TreeViewDocumentPickerSelectedItems } from "../../../../utils/components/TreeViewDocumentPicker/components/TreeViewDocumentPickerSelectedItems";
import { TreeViewDocumentPickerList } from "../../../../utils/components/TreeViewDocumentPicker/components/TreeViewDocumentPickerList";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import CertiblokTextInput from "../../../../utils/components/CertiblokTextInput";
import LanguageSelector, { SupportedLanguagesType } from "../../../../utils/components/LanguageSelector";
import GroupContactUserAutocomplete from "../../../Contacts/components/EditCreateContactGroup/GroupContactUserAutocomplete";
import { DocumentApi, SharingGroup, UserOrContact } from "certiblok-api-manager";
import UserChip from "../../../../utils/components/UserChip";
import GroupChip from "../../../../utils/components/GroupChip";
import { useMutation } from "@tanstack/react-query";
import { CertiblokDocument } from "../../../../utils/interfaces/Document";
import { apiErrorParser, instanceApi } from "../../../../utils/hooks/api";
import MLDialog from "../../../../utils/components/poppers";
import { isUserOrContact } from "../../../Folders/components/UploadDocument/functions/isUser";
import { getFullName } from "../../../../utils";
import { useEffect } from "react";
import { useBetterMediaQuery } from "../../../../utils/hooks/useBetterMediaQuery";

type ShareDocumentsDialogProps = {
	open: boolean;
	onClose: () => void;
};

const computeLatestVersion = (document: CertiblokDocument) => {
	let latestVersion = document.lastVersionFile?.[0];
	document.lastVersionFile?.forEach((version) => {
		if (version.version > latestVersion?.version) {
			latestVersion = version;
		}
	});
	return latestVersion;
};

export function ShareDocumentsDialog({ open, onClose }: ShareDocumentsDialogProps) {
	const { t } = useTranslation();
	const isLg = useBetterMediaQuery("lg");

	const { selectedDocumentsProxy, setSelectedDocuments } = useInitTreeViewDocumentPicker();

	const schema = z.object({
		notes: z.string().optional(),
		language: z.enum(["it", "en"]).optional(),
		sharedWith: z
			.object({
				users: z.array(z.custom<UserOrContact>()),
				groups: z.array(z.custom<SharingGroup>()),
				emails: z.array(z.string()),
			})
			.refine((data) => data.users.length > 0 || data.groups.length > 0 || data.emails.length > 0, {
				message: t("shareWithContact.selectShare"),
			}),
	});

	const { control, handleSubmit, reset } = useForm({
		resolver: zodResolver(schema),
		defaultValues: {
			notes: "",
			language: "it" as SupportedLanguagesType,
			sharedWith: {
				users: [] as UserOrContact[],
				groups: [] as SharingGroup[],
				emails: [] as string[],
			},
		},
	});

	useEffect(() => {
		if (open) {
			reset({
				language: "it",
				sharedWith: {
					users: [],
					groups: [],
					emails: [],
				},
				notes: "",
			});
			setSelectedDocuments([]);
		}
	}, [open]);

	const shareDocumentMutation = useMutation({
		mutationFn: ({
			documents,
			sharedWith,
			notes,
			language,
		}: { documents: CertiblokDocument[] } & z.infer<typeof schema>) => {
			if (documents.length === 0) {
				return Promise.reject(new Error(t("shareWithContact.selectDocument")));
			}
			return instanceApi(DocumentApi).shareDocumentsWithUsers({
				documents: documents.map((doc) => ({
					documentId: doc.id,
					//@ts-expect-error
					versionFileId: computeLatestVersion(doc)._id,
				})),
				shareUserIdsOrEmails: [...sharedWith.users.map((user) => user.id), ...sharedWith.emails],
				sharingGroupIds: sharedWith.groups.map((group) => group.id),
				note: notes,
				language: language,
			});
		},
		onSuccess: (res, { sharedWith }) => {
			if (res.data.succeded.length > 0) {
				MLDialog.showSnackbar(
					t("shareWithContact.shareSuccess", {
						count: res.data.succeded.length,
					}),
					{
						variant: "success",
					}
				);
			}
			res.data.failed.forEach((fail) => {
				if (fail.errorCode === "AlreadySharedWithGroup") {
					const failedGroup = sharedWith.groups.find((group) => group.id === fail.sharingGroupId);
					const failedDocument = Array.isArray(document) ? document.find((doc) => doc.id === fail.documentId) : document;
					MLDialog.showSnackbar(
						t("variousComponents.fileNameAlreadyShared", {
							documentName: failedDocument?.name ?? "",
							name: failedGroup?.name ?? "",
						}),
						{
							variant: "error",
						}
					);
				} else if (fail.errorCode === "AlreadySharedWithUser") {
					const failedUser = [...(sharedWith.users || []), ...(sharedWith.emails || [])].find((user) => {
						if (user === undefined) return false;
						if (typeof user === "string") {
							return user === fail.shareUserIdOrEmail;
						} else {
							return user.id === fail.shareUserIdOrEmail;
						}
					});
					const failedDocument = !Array.isArray(document) ? document : document.find((doc) => doc.id === fail.documentId);
					MLDialog.showSnackbar(
						t("variousComponents.fileNameAlreadyShared", {
							documentName: failedDocument?.name ?? "",
							name: isUserOrContact(failedUser) ? getFullName(failedUser) : failedUser,
						}),
						{
							variant: "error",
						}
					);
				} else {
					MLDialog.showSnackbar(fail.message ?? "", { variant: "error" });
				}
			});
			onClose();
		},
		onError: (err) => {
			MLDialog.showSnackbar((err as any)?.message ?? apiErrorParser(err), { variant: "error" });
		},
	});

	return (
		<LateralDialog
			BottomSheetProps={{
				HeaderProps: {
					className: "pb-0 px-3 pt-4",
				},
				FooterProps: {
					className: "px-3 pt-2",
				},
			}}
			open={open}
			onClose={() => {
				console.log("onClose");
				if (!shareDocumentMutation.isLoading) {
					onClose();
				}
			}}
			header={
				<Typography variant="subtitle1" className="md:p-4 md:pb-0">
					{t("dashboard.shareDocuments")}
				</Typography>
			}
			DialogContainerProps={{
				className: "w-full md:max-w-[85%] gap-3",
			}}
			className={
				"[&_.childrenContainer]:h-full [&_.childrenContainer]:flex [&_.childrenContainer]:flex-col [&_.childrenContainer]:pb-0 [&_.childrenContainer]:px-3 md:[&_.childrenContainer]:px-4 [&_.childrenContainer]:min-h-[50svh] z-[1300] h-[90svh]"
			}
			footer={
				<div className="flex justify-between items-center gap-4 md:p-6 md:pt-0">
					<Button
						onClick={() => {
							onClose();
						}}
						disabled={shareDocumentMutation.isLoading}
						variant={"outlined"}
						color="secondary"
						startIcon={<CertiblokIcon name="close" color="inherit" />}
					>
						{t("global.cancel")}
					</Button>
					<LoadingIconButton
						onClick={() => {
							handleSubmit(
								(data) => {
									shareDocumentMutation.mutate({
										documents: Array.from(selectedDocumentsProxy.values()),
										...data,
									});
								},
								(errors) => {
									Object.values(errors).forEach((error) => {
										MLDialog.showSnackbar(error.message ?? "", { variant: "error" });
									});
								}
							)();
						}}
						loading={shareDocumentMutation.isLoading}
						color="secondary"
						variant="contained"
						icon={<CertiblokIcon name="success" color="inherit" />}
					>
						{t("shareWithContact.shareCompact")}
					</LoadingIconButton>
				</div>
			}
		>
			<TreeViewDocumentPickerContextProvider
				selectedDocumentsProxy={selectedDocumentsProxy}
				setSelectedDocuments={setSelectedDocuments}
			>
				<div className="flex flex-col gap-1 md:gap-2 pb-2 md:pt-2">
					<Controller
						control={control}
						name="sharedWith"
						render={({ field }) => {
							return (
								<>
									<GroupContactUserAutocomplete
										onSelectUser={(user) => field.onChange({ ...field.value, users: [...field.value.users, user] })}
										onSelectGroup={(group) => field.onChange({ ...field.value, groups: [...field.value.groups, group] })}
										onSelectEmail={(email) => field.onChange({ ...field.value, emails: [...field.value.emails, email] })}
										includeGroups
										includeEmails
										disabledSharingGroupsIds={field.value.groups.map((group) => group.id)}
										disabledUsersOrContactsIds={field.value.users.map((user) => user.id)}
										disabledEmails={field.value.emails}
										TextFieldProps={{
											InputProps: {
												placeholder: t("shareWithContact.selectShare"),
											},
										}}
									/>
									{(field.value.emails.length > 0 || field.value.groups.length > 0 || field.value.users.length > 0) && (
										<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1 max-h-[80px] overflow-y-auto">
											{field.value.emails.map((elem, index) => {
												return (
													<div key={elem + index}>
														<UserChip
															user={elem}
															deletable
															onDelete={() =>
																field.onChange({ ...field.value, emails: field.value.emails.filter((email) => email !== elem) })
															}
														/>
													</div>
												);
											})}
											{field.value.groups.map((elem, index) => {
												return (
													<div key={elem.id + index}>
														<GroupChip
															group={elem}
															onDelete={(elem) => {
																field.onChange({ ...field.value, groups: field.value.groups.filter((group) => group.id !== elem.id) });
															}}
														/>
													</div>
												);
											})}
											{field.value.users.map((elem, index) => {
												return (
													<div key={elem.id + index}>
														<UserChip
															user={elem}
															deletable
															onDelete={() =>
																field.onChange({ ...field.value, users: field.value.users.filter((user) => user.id !== elem.id) })
															}
														/>
													</div>
												);
											})}
										</div>
									)}
								</>
							);
						}}
					/>
					<div className="flex items-center justify-between w-full">
						<Typography variant="label">{t("request.selectLanguage")}</Typography>
						<Controller
							control={control}
							name="language"
							render={({ field }) => <LanguageSelector language={field.value} onLanguageChange={field.onChange} />}
						/>
					</div>
				</div>
				<div className="flex flex-col flex-1 border-[1px] border-secondary rounded-lg p-2 border-solid">
					<TreeViewDocumentPickerAutocomplete className="pb-2" />
					<TreeViewDocumentPickerSelectedItems />
					<TreeViewDocumentPickerList />
				</div>
				<div className="py-2 md:py-4 pt-2 md:pt-4">
					<Controller
						control={control}
						name="notes"
						render={({ field }) => (
							<CertiblokTextInput
								label={t("documents.insertNote")}
								color="secondary"
								variant="compact"
								fullWidth
								multiline
								rows={isLg ? 4 : 1}
								value={field.value}
								onChange={(e) => field.onChange(e.target.value)}
								placeholder={t("documents.insertNote")}
							/>
						)}
					/>
				</div>
			</TreeViewDocumentPickerContextProvider>
		</LateralDialog>
	);
}

export const ShareDocumentsDialogWithModalNavigator = ModalNavigator.create<
	Omit<ShareDocumentsDialogProps, "open" | "onClose">
>(() => {
	const { visible } = useModalRoute();
	const { popModal } = useModalNavigator();

	return <ShareDocumentsDialog open={visible} onClose={popModal} />;
});
